/*
 * Copyright (C) 2018 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { map } from 'rxjs/operators';
import { Component } from '@angular/core';
import { State } from 'app/main/store';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  AddReadOnlyRole,
  DeleteSelectedTenantDetails,
  LoadTenantDetails,
  UpdateTenantDetails
} from 'app/page-modules/tenant-management/store/api/tenant-management-api.actions';
import * as fromTenants from 'app/page-modules/tenant-management/store/tenant-management.selectors';
import { ActivatedRoute } from '@angular/router';
import {
  TenantDetails,
  TenantStatus,
  AuditRequestReason
} from 'app/page-modules/tenant-management/models';
import { filterUndefined } from 'app/shared/rxjs/rxjs.utils';
import { NavigationItem } from 'app/shared/components/menu-list/navigation-item';
import * as fromShared from 'app/user-auth/store/user-auth.selectors';
import { LoginToTenant, AuditedLoginToTenant } from 'app/user-auth/store/user-auth.actions';
import { AuthService } from 'app/user-auth/services/auth.service';
import { UserDetails } from 'app/user-auth/models';
import { MatDialog } from '@angular/material/dialog';
import { TenantLoginReasonPanelComponent } from 'app/shared/components/tenant-login-reason-panel/tenant-login-reason-panel.component';
import * as activityAction from 'app/page-modules/tenant-management/modules/activity/store/activity.actions';
import * as clientFilesAction from 'app/page-modules/tenant-management/modules/client-files/store/client-files.actions';
import * as detailsAction from 'app/page-modules/tenant-management/modules/details/store/details.actions';
import * as valuationDatesAction from 'app/page-modules/tenant-management/modules/valuation-dates/store/valuation-dates.actions';

@Component({
  selector: 'og-tenant-view',
  templateUrl: './tenant-view.component.html',
  styleUrls: ['./tenant-view.component.scss'],
  standalone: false
})
export class TenantViewComponent {
  confirmationDialogText$: Observable<string>;
  tenantDetails$: Observable<TenantDetails>;
  isTenantDataLoading$: Observable<boolean>;
  userDetails$: Observable<UserDetails>;

  deletionInProgress = false;
  showDeleteConfirmationPanel = false;

  menuItems: NavigationItem[] = [
    {
      displayName: 'Activity',
      iconName: 'storage',
      routerLink: ['activity']
    },
    {
      displayName: 'Broker Emails',
      iconName: 'email',
      routerLink: ['details/broker-emails']
    },
    {
      displayName: 'Calculations',
      iconName: 'list_alt',
      children: [
        {
          displayName: 'Schedule',
          routerLink: ['calculations/schedule']
        },
        {
          displayName: 'Runs',
          routerLink: ['calculations/runs']
        },
        {
          displayName: 'Trigger',
          routerLink: ['calculations/trigger']
        }
      ]
    },
    {
      displayName: 'Client Files',
      iconName: 'folder',
      routerLink: ['client-files']
    },
    {
      displayName: 'Valuation Dates',
      iconName: 'calendar_today',
      routerLink: ['valuation-dates']
    },
    {
      displayName: 'Config',
      iconName: 'dns',
      children: [
        {
          displayName: 'Generic editor',
          routerLink: ['details/config-editor']
        },
        {
          displayName: 'Service Component State',
          routerLink: ['details/service-component-state']
        },
        {
          displayName: 'Treasury',
          children: [
            {
              displayName: 'Database Setup',
              routerLink: ['details/treasury/database-setup']
            }
          ]
        },
        {
          displayName: 'BI',
          children: [
            {
              displayName: 'Infrastructure Setup',
              routerLink: ['details/bi/infrastructure-setup']
            },
            {
              displayName: 'Dashboards',
              routerLink: ['details/bi/dashboards']
            }
          ]
        },
        {
          displayName: 'Reporting Currency',
          routerLink: ['details/reporting-currency']
        },
        {
          displayName: 'Permissions',
          routerLink: ['details/permissions']
        },
        {
          displayName: 'Collateral',
          routerLink: ['details/collateral']
        },
        {
          displayName: 'Tags',
          routerLink: ['details/tags']
        },
        {
          displayName: 'E-mail domains',
          routerLink: ['details/email-domains']
        },
        {
          displayName: 'E-mail types',
          routerLink: ['details/email-types']
        },
        {
          displayName: 'Flags',
          routerLink: ['details/flags']
        },
        {
          displayName: 'Limits',
          routerLink: ['details/limits']
        },
        {
          displayName: 'Attribution groupings',
          routerLink: ['details/margin-attribution-groupings']
        },
        {
          displayName: 'Monitoring',
          routerLink: ['details/monitoring']
        },
        {
          displayName: 'OTC Rebalance Currencies',
          routerLink: ['details/rebalance/otc-rebalance-currencies']
        },
        {
          displayName: 'Portfolio File Config',
          routerLink: ['details/portfolio-file-config']
        },
        {
          displayName: 'Portfolio Margin Models',
          routerLink: ['details/portfolio-margin-models']
        },
        {
          displayName: 'Reporting Specifications',
          routerLink: ['details/reporting-specifications']
        },
        {
          displayName: 'Secret Management',
          routerLink: ['details/secret-management']
        },
        {
          displayName: 'Screens',
          routerLink: ['details/screens']
        },
        {
          displayName: 'History',
          routerLink: ['details/history']
        }
      ]
    },
    {
      displayName: 'SFTP Servers',
      iconName: 'domain',
      routerLink: ['details/sftp-servers']
    },
    {
      displayName: 'What-If Server',
      iconName: 'domain',
      routerLink: ['details/what-if-server']
    },
    {
      displayName: 'Users',
      iconName: 'accessibility',
      routerLink: ['users']
    }
  ];

  constructor(
    private store: Store<State>,
    route: ActivatedRoute,
    private authService: AuthService,
    private matDialog: MatDialog
  ) {
    this.tenantDetails$ = store.pipe(select(fromTenants.getSelectedTenantData), filterUndefined());
    this.isTenantDataLoading$ = store.pipe(select(fromTenants.isSelectedTenantDataLoading));
    this.userDetails$ = store.pipe(select(fromShared.getUserDetailsData), filterUndefined());
    const tenantId = route.snapshot.params.tenantId;
    if (tenantId) {
      // Clear state in other modules' stores before loading new tenant details.
      // This is safe as actions are processed synchronously in NgRx.
      this.store.dispatch(new activityAction.ClearState());
      this.store.dispatch(new clientFilesAction.ClearState());
      this.store.dispatch(new detailsAction.ClearState());
      this.store.dispatch(new valuationDatesAction.ClearState());
      this.store.dispatch(new LoadTenantDetails(tenantId));
    }

    this.confirmationDialogText$ = store.pipe(
      select(fromTenants.getSelectedTenantData),
      filterUndefined(),
      map(({ name }) => `Confirm deletion of ${name}`)
    );
  }

  onReadOnlyRoleSetupClick(): void {
    this.store.dispatch(new AddReadOnlyRole());
  }

  onUserDeletion(): void {
    this.deletionInProgress = true;
    this.store.dispatch(new DeleteSelectedTenantDetails());
  }

  onTenantStatusUpdate(newStatus: TenantStatus): void {
    this.store.dispatch(
      new UpdateTenantDetails('status', `Tenant successfully ${newStatus}`, {
        tenantDetails: {
          status: newStatus
        }
      })
    );
  }

  canEnableTenant(tenantDetails: TenantDetails): boolean {
    return tenantDetails.status === 'disabled';
  }

  canDisableTenant(tenantDetails: TenantDetails): boolean {
    return tenantDetails.status === 'enabled';
  }

  canLogin(userDetails: UserDetails, tenantDetails: TenantDetails): boolean {
    return this.authService.canLoginToTenant(userDetails, tenantDetails);
  }

  loginToTenant(tenantDetails: TenantDetails): void {
    if (this.authService.requiresLoginReason(tenantDetails)) {
      const ref = this.matDialog.open(TenantLoginReasonPanelComponent);
      ref.componentInstance.saveClick.subscribe((auditDetails: AuditRequestReason) => {
        this.store.dispatch(new AuditedLoginToTenant(tenantDetails, undefined, auditDetails));
        ref.close();
      });
    } else {
      this.store.dispatch(new LoginToTenant(tenantDetails));
    }
  }

  shouldDisableSidenavMenu(tenantDetails: TenantDetails): boolean {
    return tenantDetails.status === 'provisioning';
  }

  sidenavMenuDisabledReason(tenantDetails: TenantDetails): string {
    return `Tenant ${tenantDetails.organizationId} is being provisioned`;
  }
}
